import type { MaybeRefOrGetter } from 'vue'
import type { FaqAnswerBlock, FaqDefinition, FaqItem, FaqVisibilityRules } from '~/features/my-wallet/types/faq'
import { useI18n } from 'vue-i18n'
import { answerToPlainText, parseEmphasis } from '~/features/my-wallet/utils/faq'

interface UseFaqItemsOptions<TVisibility extends string, TContext> {
  definitions: FaqDefinition<TVisibility>[]
  visibilityRules: FaqVisibilityRules<TVisibility, TContext>
  keyPath: string
  /** `null` = no data yet, so no questions. */
  context: MaybeRefOrGetter<TContext | null>
}

/** Block as it comes from the YAML: a single key, `paragraph`, `bullets` or `numbered`. */
type RawAnswerBlock = Partial<Record<FaqAnswerBlock['kind'], unknown>>

export function useFaqItems<TVisibility extends string, TContext>(options: UseFaqItemsOptions<TVisibility, TContext>) {
  const { t, tm, rt } = useI18n()

  // `tm` returns the raw message tree; `rt` turns each message into text.
  type RawMessage = Parameters<typeof rt>[0]

  function translateMessage(message: unknown) {
    return parseEmphasis(rt(message as RawMessage))
  }

  function translateList(messages: unknown) {
    return Array.isArray(messages) ? messages.map(translateMessage) : []
  }

  function toAnswerBlock(rawBlock: RawAnswerBlock): FaqAnswerBlock | null {
    if (rawBlock.paragraph !== undefined)
      return { kind: 'paragraph', text: translateMessage(rawBlock.paragraph) }
    if (rawBlock.bullets !== undefined)
      return { kind: 'bullets', items: translateList(rawBlock.bullets) }
    if (rawBlock.numbered !== undefined)
      return { kind: 'numbered', items: translateList(rawBlock.numbered) }
    return null
  }

  function translateAnswer(keyPath: string): FaqAnswerBlock[] {
    const rawBlocks = tm(keyPath) as unknown
    if (!Array.isArray(rawBlocks))
      return []
    return (rawBlocks as RawAnswerBlock[])
      .map(toAnswerBlock)
      .filter((answerBlock): answerBlock is FaqAnswerBlock => answerBlock !== null)
  }

  return computed<FaqItem[]>(() => {
    const context = toValue(options.context)
    if (!context)
      return []

    return options.definitions
      .filter(({ visibility }) => options.visibilityRules[visibility](context))
      .map(({ key }) => {
        const faqKeyPath = `${options.keyPath}.${key}`
        const question = t(`${faqKeyPath}.question`)
        const answer = translateAnswer(`${faqKeyPath}.answer`)
        return {
          key,
          question,
          answer,
          searchText: `${question} ${answerToPlainText(answer)}`,
        }
      })
  })
}
